import React from 'react';
import Icon from '../AppIcon';

const ConnectionStatusIndicator = ({ 
  status = 'disconnected',
  quality = 'good',
  lastUpdate = null,
  fallbackActive = false,
  className = ''
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          color: 'text-emerald-500',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/20',
          icon: 'Wifi',
          label: 'Connected',
          description: fallbackActive ? 'Simulation Mode' : 'Live Data'
        };
      case 'connecting':
        return {
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/20',
          icon: 'Loader2',
          label: 'Connecting...',
          description: 'Establishing connection',
          animate: true
        };
      case 'degraded':
        return {
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/20',
          icon: 'AlertTriangle',
          label: 'Limited',
          description: 'Fallback active'
        };
      case 'error':
        return {
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/20',
          icon: 'AlertCircle',
          label: 'Error',
          description: 'Connection failed'
        };
      default:
        return {
          color: 'text-slate-500',
          bgColor: 'bg-slate-500/10',
          borderColor: 'border-slate-500/20',
          icon: 'WifiOff',
          label: 'Disconnected',
          description: 'No connection'
        };
    }
  };

  const getQualityIndicator = () => {
    switch (quality) {
      case 'excellent':
        return { dots: 4, color: 'bg-emerald-500' };
      case 'good':
        return { dots: 3, color: 'bg-emerald-500' };
      case 'fair':
        return { dots: 2, color: 'bg-amber-500' };
      case 'poor':
        return { dots: 1, color: 'bg-red-500' };
      default:
        return { dots: 0, color: 'bg-slate-300' };
    }
  };

  const statusConfig = getStatusConfig();
  const qualityConfig = getQualityIndicator();

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      
      if (diffSeconds < 60) return `${diffSeconds}s ago`;
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      return date?.toLocaleTimeString();
    } catch (error) {
      return null;
    }
  };

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Status Indicator */}
      <div className={`
        flex items-center space-x-2 px-3 py-1.5 rounded-lg border
        ${statusConfig?.bgColor} ${statusConfig?.borderColor}
      `}>
        <Icon 
          name={statusConfig?.icon} 
          size={14} 
          className={`${statusConfig?.color} ${statusConfig?.animate ? 'animate-spin' : ''}`} 
        />
        <div className="flex flex-col">
          <span className={`text-xs font-medium ${statusConfig?.color}`}>
            {statusConfig?.label}
          </span>
          <span className="text-xs text-muted-foreground">
            {statusConfig?.description}
          </span>
        </div>
      </div>
      {/* Quality Indicator */}
      {status === 'connected' && (
        <div className="flex items-center space-x-1">
          {[...Array(4)]?.map((_, i) => (
            <div
              key={i}
              className={`
                w-1 rounded-full
                ${i < qualityConfig?.dots ? qualityConfig?.color : 'bg-slate-300'}
                ${i === 0 ? 'h-2' : i === 1 ? 'h-3' : i === 2 ? 'h-4' : 'h-5'}
              `}
            />
          ))}
        </div>
      )}
      {/* Last Update */}
      {lastUpdate && (
        <span className="text-xs text-muted-foreground">
          {formatLastUpdate(lastUpdate)}
        </span>
      )}
    </div>
  );
};

export default ConnectionStatusIndicator;